import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Github, ArrowUpRight, Instagram, Facebook } from "lucide-react";
import { CONTACT, NAV_LINKS, SERVICES } from "@/lib/site-data";

// TikTok icon (not in lucide-react)
const TikTokIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-slate-950 text-slate-300">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 grid-lines-dark">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 bg-white p-[2px]">
                <img src="/cetc-logo.jpg" alt="Cutting Edge Technologies logo" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-display text-white text-lg tracking-tight">Cutting Edge</div>
                <div className="eyebrow text-slate-500">Technologies Consulting</div>
              </div>
            </div>
            <p className="mt-8 text-slate-400 text-base leading-relaxed max-w-md">
              We empower businesses through technology, talent, and training — partnering with teams
              of every size to modernize, scale, and win.
            </p>
            <div className="mt-8 flex items-center gap-3">
              {[
                { Icon: Linkedin, href: "#", label: "linkedin" },
                { Icon: Facebook, href: "#", label: "facebook" },
                { Icon: Instagram, href: "#", label: "instagram" },
                { Icon: TikTokIcon, href: "#", label: "tiktok" },
                { Icon: Twitter, href: "#", label: "twitter" },
                { Icon: Github, href: "#", label: "github" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  data-testid={`social-${label}`}
                  aria-label={label}
                  className="h-10 w-10 border border-slate-800 flex items-center justify-center text-slate-400 hover:text-emerald-400 hover:border-emerald-500 transition-colors"
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow text-slate-500 mb-5">Navigate</div>
            <ul className="space-y-3">
              {NAV_LINKS.slice(0, 6).map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-slate-300 hover:text-emerald-400 text-sm transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="eyebrow text-slate-500 mb-5">Services</div>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services#${s.slug}`} className="text-slate-300 hover:text-emerald-400 text-sm transition-colors">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow text-slate-500 mb-5">Headquarters</div>
            <address className="not-italic text-sm text-slate-300 leading-relaxed">
              {CONTACT.address}
            </address>
            <div className="mt-5 space-y-2 text-sm">
              <a href={CONTACT.phoneHref} className="block text-slate-300 hover:text-emerald-400 transition-colors">
                {CONTACT.phone}
              </a>
              <a href={CONTACT.emailHref} className="block text-slate-300 hover:text-emerald-400 transition-colors">
                {CONTACT.email}
              </a>
            </div>
            <Link
              to="/contact"
              className="mt-6 inline-flex items-center gap-2 text-emerald-400 text-sm font-medium border-b border-emerald-500/40 pb-1 hover:border-emerald-400"
              data-testid="footer-contact-cta"
            >
              Start a conversation <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-slate-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="text-xs text-slate-500 font-mono">
            © {new Date().getFullYear()} CUTTING EDGE TECHNOLOGIES CONSULTING USA. ALL RIGHTS RESERVED.
          </div>
          <div className="text-xs text-slate-500 font-mono">
            Built for teams that ship.
          </div>
        </div>
      </div>
    </footer>
  );
}
